import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaCheckCircle,
  FaCode,
  FaCubes,
  FaDesktop,
  FaGamepad,
  FaMapMarkedAlt,
  FaMobileAlt,
  FaRobot,
  FaShoppingCart,
  FaStore,
  FaVrCardboard
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
  bestFor: string[];
  category: string;
}

const Services: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const services: Service[] = [
    {
      id: 'web-apps',
      title: 'Web Application Development',
      description: 'Modern websites, dashboards, admin panels, catalogs, and web products built for real business use.',
      icon: 'FaCode',
      features: ['Responsive websites', 'React-based web apps', 'Admin panels & dashboards', 'API & payment integration'],
      bestFor: ['Startups', 'Service businesses', 'E-commerce brands'],
      category: 'apps'
    },
    {
      id: 'mobile-apps',
      title: 'Mobile App Development',
      description: 'iOS, Android, and cross-platform apps with clean UI, scalable backend, and store-ready delivery.',
      icon: 'FaMobileAlt',
      features: ['Android & iOS apps', 'Flutter / React Native', 'Secure backend architecture', 'Play Store & App Store support'],
      bestFor: ['Startups', 'Retail brands', 'Enterprise tools'],
      category: 'apps'
    },
    {
      id: 'game-development',
      title: 'Game Development & Monetization',
      description: 'Mobile and PC games with Unity production, ads, in-app purchases, analytics, and publishing support.',
      icon: 'FaGamepad',
      features: ['2D & 3D games', 'AdMob & rewarded ads', 'In-app purchases', 'Play Store publishing'],
      bestFor: ['Creators', 'Publishers', 'Game startups'],
      category: 'games'
    },
    {
      id: 'ar-commerce',
      title: 'AR Solutions for E-Commerce',
      description: 'Try-before-you-buy product previews that help customers view products in their own space.',
      icon: 'FaVrCardboard',
      features: ['AR product preview', 'Mobile AR experiences', '3D model optimization', 'Shopify / website integration'],
      bestFor: ['Furniture brands', 'D2C stores', 'Retail products'],
      category: 'arvr'
    },
    {
      id: 'vr-training',
      title: 'Simulation & VR Training',
      description: 'Interactive VR simulations for training, safety, driving, operations, and skill-based learning.',
      icon: 'FaDesktop',
      features: ['Vehicle simulation', 'Industrial training modules', 'VR training environments', 'Performance tracking'],
      bestFor: ['EdTech', 'Industry training', 'Skill centers'],
      category: 'arvr'
    },
    {
      id: 'walkthroughs',
      title: '3D Walkthrough & Virtual Tours',
      description: 'Immersive walkthroughs and web-based virtual tours for properties, spaces, venues, and campuses.',
      icon: 'FaMapMarkedAlt',
      features: ['3D architectural walkthroughs', '360 virtual tours', 'VR compatible tours', 'Smooth camera animations'],
      bestFor: ['Real estate', 'Hotels & resorts', 'Commercial spaces'],
      category: 'visual'
    },
    {
      id: 'architecture-visuals',
      title: 'Architectural Visualization',
      description: 'High-end exterior, interior, floor plan, material, and lighting visuals before construction starts.',
      icon: 'FaBuilding',
      features: ['Photorealistic exterior renders', 'Interior visualization', '3D floor plans', 'Lighting & material setup'],
      bestFor: ['Architects', 'Builders', 'Interior designers'],
      category: 'visual'
    },
    {
      id: 'product-visuals',
      title: 'Product Visualization',
      description: 'E-commerce-ready product renders, 3D models, 360 views, animations, and AR shopping assets.',
      icon: 'FaShoppingCart',
      features: ['3D product modeling', 'Photorealistic product renders', '360 product views', 'Promo animations'],
      bestFor: ['Amazon sellers', 'Shopify stores', 'Manufacturers'],
      category: 'visual'
    },
    {
      id: 'virtual-showrooms',
      title: 'Virtual Showroom Development',
      description: 'Interactive digital showrooms where customers can explore, customize, and experience products in 3D.',
      icon: 'FaStore',
      features: ['Interactive 3D showrooms', 'Product configurators', 'VR & web delivery', 'Cross-platform compatibility'],
      bestFor: ['Furniture brands', 'Automobile showrooms', 'Luxury products'],
      category: 'arvr'
    },
    {
      id: 'touchscreen-apps',
      title: 'Interactive Touchscreen Applications',
      description: 'Touchscreen kiosks and display apps for retail, exhibitions, maps, directories, and product catalogs.',
      icon: 'FaCubes',
      features: ['Kiosk applications', 'Interactive catalogs', 'Event & exhibition displays', 'Maps & directories'],
      bestFor: ['Retail stores', 'Trade shows', 'Corporate offices'],
      category: 'business'
    },
    {
      id: 'ai-integration',
      title: 'AI Integration for Business',
      description: 'Useful AI features for automation, support, recommendations, analytics, and intelligent workflows.',
      icon: 'FaRobot',
      features: ['AI chatbots & assistants', 'Business automation', 'Recommendation systems', 'Computer vision apps'],
      bestFor: ['E-commerce', 'Healthcare', 'Service businesses'],
      category: 'business'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'apps', name: 'Web & Mobile' },
    { id: 'games', name: 'Games' },
    { id: 'arvr', name: 'AR/VR' },
    { id: 'visual', name: '3D Visuals' },
    { id: 'business', name: 'AI & Kiosks' }
  ];

  const iconMap: { [key: string]: JSX.Element } = {
    FaCode: <FaCode className="text-4xl" />,
    FaMobileAlt: <FaMobileAlt className="text-4xl" />,
    FaGamepad: <FaGamepad className="text-4xl" />,
    FaVrCardboard: <FaVrCardboard className="text-4xl" />,
    FaDesktop: <FaDesktop className="text-4xl" />,
    FaMapMarkedAlt: <FaMapMarkedAlt className="text-4xl" />,
    FaBuilding: <FaBuilding className="text-4xl" />,
    FaShoppingCart: <FaShoppingCart className="text-4xl" />,
    FaStore: <FaStore className="text-4xl" />,
    FaCubes: <FaCubes className="text-4xl" />,
    FaRobot: <FaRobot className="text-4xl" />
  };

  const filteredServices = activeCategory === 'all'
    ? services
    : services.filter((service) => service.category === activeCategory);

  const servicesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'RedRender Services',
    url: `${siteUrl}/services`,
    itemListElement: services.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Service',
        name: service.title,
        description: service.description,
        provider: {
          '@type': 'Organization',
          name: 'RedRender',
          url: siteUrl
        }
      }
    }))
  };

  const getCategoryName = (categoryId: string) => {
    return categories.find((category) => category.id === categoryId)?.name || categoryId;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black pt-24 pb-20">
      <SEO
        title="Services - Web, Mobile, Game, AR/VR, 3D & AI Development | RedRender"
        description="Explore RedRender services including web and mobile app development, game development, AR/VR solutions, 3D visualization, virtual showrooms, kiosks, and AI integration."
        path="/services"
        keywords={[
          'RedRender services',
          'web development services',
          'mobile app development services',
          'game development services',
          'AR VR solutions',
          '3D visualization services',
          'AI integration services'
        ]}
        jsonLd={servicesJsonLd}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(239,68,68,0.16),transparent_28rem),radial-gradient(circle_at_80%_22%,rgba(239,68,68,0.09),transparent_24rem),linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:auto,auto,96px_96px,96px_96px]" />
      <div className="pointer-events-none absolute left-0 top-36 h-72 w-72 rounded-full bg-red-500/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-20 right-0 h-80 w-80 rounded-full bg-red-400/10 blur-3xl" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-14 max-w-4xl text-center animate-slide-up">
          <div className="mb-6 inline-flex items-center space-x-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
            <span className="h-2 w-2 rounded-full bg-red-500 shadow-lg shadow-red-500/60" />
            <span className="font-display text-sm font-semibold text-red-400">What We Should Sell</span>
          </div>
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
            Focused <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-gray-400 sm:text-xl">
            Selected service packages from RedRender's strongest work: apps, games, AR/VR, 3D visualization, AI, and interactive business experiences.
          </p>
        </div>

        <div className="mb-12 flex flex-wrap justify-center gap-3 animate-slide-up">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveCategory(category.id)}
              className={`rounded-full px-6 py-3 font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/40'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:border-red-500/40 hover:bg-white/10 hover:text-white'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredServices.map((service, index) => (
            <article
              key={service.id}
              className="group relative overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-red-500/50 hover:shadow-2xl hover:shadow-red-500/15 animate-scale-in"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500/70 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-red-500/10 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />

              <div className="relative">
                <div className="mb-6 flex items-center justify-between">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-lg border border-red-500/20 bg-red-500/10 text-red-400 transition-all duration-300 group-hover:scale-105 group-hover:border-red-500/50 group-hover:text-red-300">
                    {iconMap[service.icon]}
                  </div>
                  <span className="rounded-full border border-white/10 bg-black/30 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                    {getCategoryName(service.category)}
                  </span>
                </div>

                <h3 className="mb-3 font-display text-2xl font-bold text-white transition-colors group-hover:text-red-300">
                  {service.title}
                </h3>

                <p className="mb-6 min-h-[84px] leading-relaxed text-gray-400">
                  {service.description}
                </p>

                <ul className="mb-6 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-sm text-gray-300">
                      <FaCheckCircle className="mr-3 flex-shrink-0 text-red-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mb-7">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">Ideal For</p>
                  <div className="flex flex-wrap gap-2">
                    {service.bestFor.map((item) => (
                      <span key={item} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-gray-300">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center font-semibold text-red-400 transition-colors duration-300 hover:text-red-300"
                >
                  Get Started
                  <FaArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <section className="overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-red-500/15 via-white/[0.04] to-black p-8 text-center backdrop-blur-xl sm:p-12 animate-slide-up">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-red-300">Best starting point</p>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Start with AR/VR, 3D visualization, web/mobile, or game development.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
            These services are clear, client-friendly, and match RedRender's strongest visual and technical work.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center rounded-full bg-gradient-to-r from-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-lg shadow-red-500/30 transition-all duration-300 hover:shadow-red-500/50"
          >
            Get a Free Consultation
            <FaArrowRight className="ml-2" />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Services;
